/**
 * Fila de aprovação — a decisão que justifica o blindado ter aprovação manual.
 *
 * Cada card traz o que o gestor precisa DECIDIR AGORA: quem é o cliente e há
 * quanto tempo, o score de risco CONGELADO e explicado fator a fator, a margem
 * estimada (estimativa declarada, não contabilidade) e a disponibilidade real
 * do período. O SLA é visual: acima de 4 horas na fila o tempo acende — o
 * prompt pede alerta, e a cor NUNCA vem sozinha (o número de horas está no
 * texto).
 *
 * A fila se renova sozinha a cada minuto (o `refetchInterval` do hook): é a
 * tela que fica aberta na mesa do gestor.
 */

import {
  ROTULO_DA_VERIFICACAO_EXTERNA,
  ROTULO_DO_ALERTA_DE_RISCO,
  ROTULO_DO_DOCUMENTO_SOLICITAVEL,
  ROTULO_DO_FATOR_DE_RISCO,
  ROTULO_DO_STATUS_DE_DOCUMENTO,
} from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import {
  CheckCheck,
  Clock,
  FileQuestion,
  LoaderCircle,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useIntegracaoMiraScore } from '@/features/analise-cadastral/api'
import { QuadroDaAnalise } from '@/features/analise-cadastral/quadro-da-analise'
import { BadgeDeRisco } from '@/features/reservas/badges'
import {
  useAprovar,
  useFilaDeAprovacao,
  useRecusar,
  useSolicitarDocumento,
  type ItemDaFila,
} from '@/features/reservas/api'
import { errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'

/** Acima disto, o tempo na fila acende. O alerta de SLA que o prompt pede. */
const SLA_EM_HORAS = 4

const TIPOS_DE_DOCUMENTO = [
  ['CNH', 'CNH'],
  ['COMPROVANTE_DE_ENDERECO', 'Comprovante de endereço'],
  ['COMPROVANTE_DE_RENDA', 'Comprovante de renda'],
  ['SELFIE', 'Selfie de verificação'],
  ['CONTRATO_SOCIAL', 'Contrato social'],
  ['OUTRO', 'Outro documento'],
] as const

export function FilaDeAprovacaoPage(): ReactElement {
  const fila = useFilaDeAprovacao()

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Fila de aprovação</h1>
        <p className="text-sm text-fg-secondary">
          Blindado tem perfil de risco próprio — toda reserva passa por uma decisão humana. O score
          ordena e explica; quem decide é você.
        </p>
      </div>

      {fila.isPending ? <LoadingState rotulo="Carregando a fila…" /> : null}
      {fila.isError ? (
        <ErrorState
          titulo="Não foi possível carregar a fila"
          erro={fila.error}
          aoTentarNovamente={() => {
            void fila.refetch()
          }}
        />
      ) : null}

      {fila.data?.itens.length === 0 ? (
        <EmptyState
          icone={CheckCheck}
          titulo="Fila vazia"
          descricao="Nenhuma reserva aguardando análise. Quando um pagamento confirmar, ela aparece aqui com o risco já apurado."
        />
      ) : null}

      <div className="space-y-4">
        {(fila.data?.itens ?? []).map((item) => (
          <CardDaFila key={item.reserva.id} item={item} />
        ))}
      </div>
    </div>
  )
}

function CardDaFila({ item }: { readonly item: ItemDaFila }): ReactElement {
  const aprovar = useAprovar(item.reserva.id)
  const recusar = useRecusar(item.reserva.id)
  const solicitar = useSolicitarDocumento(item.reserva.id)
  // Com o Mira ligado, o quadro dele substitui os "não consultado" do score
  // interno — são as MESMAS fontes, agora consultadas de verdade.
  const integracao = useIntegracaoMiraScore()
  const miraLigado = integracao.data !== undefined && integracao.data.modo !== 'DESLIGADO'

  const [recusando, setRecusando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [pedindoDocumento, setPedindoDocumento] = useState(false)
  const [tipoDeDocumento, setTipoDeDocumento] = useState<string>('CNH')
  const [instrucoes, setInstrucoes] = useState('')

  const estourouSla = item.horasNaFila >= SLA_EM_HORAS

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Link
              to="/reservas/$reservaId"
              params={{ reservaId: item.reserva.id }}
              className="underline-offset-4 hover:underline"
            >
              Reserva nº {item.reserva.numero}
            </Link>
            {item.risco === null ? null : (
              <BadgeDeRisco score={item.risco.score} nivel={item.risco.nivel} />
            )}
          </span>
          <span
            className={`flex items-center gap-1 text-sm font-normal ${estourouSla ? 'text-danger-fg' : 'text-fg-secondary'}`}
          >
            <Clock className="size-4" />
            {item.horasNaFila.toFixed(1)}h na fila
            {estourouSla ? ' — acima do SLA' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-fg-muted">Cliente</p>
            <p className="font-medium">{item.conta.nome}</p>
            <p className="text-xs text-fg-secondary">
              {item.conta.tipo} · desde {formatarData(item.conta.clienteDesde)} ·{' '}
              {item.conta.reservasFinalizadas} locação(ões)
              {item.conta.noShows > 0 ? ` · ${String(item.conta.noShows)} no-show(s)` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-fg-muted">Período</p>
            <p className="font-medium">{item.reserva.categoria.nome}</p>
            <p className="text-xs text-fg-secondary">
              {formatarDataHora(item.reserva.retirada)} → {formatarDataHora(item.reserva.devolucao)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-fg-muted">Números</p>
            <p className="font-medium tabular-nums">{formatarMoedaDoWire(item.reserva.total)}</p>
            <p className="text-xs text-fg-secondary tabular-nums">
              margem estimada{' '}
              {item.margemEstimada === null ? '—' : formatarMoedaDoWire(item.margemEstimada)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-fg-muted">Disponibilidade real</p>
            <p className="font-medium">
              {item.disponibilidadeReal === null
                ? '—'
                : `${String(item.disponibilidadeReal)} unidade(s) livre(s)`}
            </p>
            {/* ⚠️ Quase sempre ausente — e quando aparece é a informação mais
                importante do cartão. "0 unidade(s) livre(s)" sozinho lê como
                "no limite"; ele é o mesmo texto de uma janela vendida a mais,
                porque `disponibilidadeReal` tem piso em zero. */}
            {item.excedenteDaJanela === null || item.excedenteDaJanela === 0 ? null : (
              <p className="mt-0.5 flex items-start gap-1.5 text-xs font-medium text-warning-fg">
                <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                <span>
                  Janela vendida além da frota:{' '}
                  <span className="tabular-nums">{item.excedenteDaJanela}</span>{' '}
                  {item.excedenteDaJanela === 1 ? 'reserva' : 'reservas'} sem carro previsto.
                </span>
              </p>
            )}
            {item.condutorPrincipal === null ? null : (
              <p className="text-xs text-fg-secondary">
                CNH {item.condutorPrincipal.cnhNumero} até{' '}
                {formatarData(item.condutorPrincipal.cnhValidade)}
              </p>
            )}
          </div>
        </div>

        {item.risco !== null &&
        (item.risco.contribuicoes.length > 0 || item.risco.alertas.length > 0) ? (
          <div className="flex flex-wrap gap-2">
            {item.risco.alertas.map((alerta) => (
              <Badge key={alerta} variant="danger">
                {rotuloDeAlerta(alerta)}
              </Badge>
            ))}
            {item.risco.contribuicoes.map((contribuicao) => (
              <Badge key={contribuicao.fator} variant="warning">
                {rotuloDeFator(contribuicao.fator)} (+{contribuicao.pontos})
              </Badge>
            ))}
          </div>
        ) : null}

        {miraLigado ? (
          <QuadroDaAnalise analise={item.analiseCadastral} reservaId={item.reserva.id} />
        ) : null}

        {/* O que NENHUM provedor conferiu, dito em voz alta: "risco baixo" com
            tudo não consultado não é "passou nas verificações". */}
        {miraLigado || item.risco?.verificacoesExternas === undefined ? null : (
          <div className="flex flex-wrap gap-2">
            {item.risco.verificacoesExternas.map((verificacao) => (
              <Badge
                key={verificacao.verificacao}
                variant={
                  verificacao.resultado === 'APROVADO'
                    ? 'success'
                    : verificacao.resultado === 'REPROVADO'
                      ? 'danger'
                      : 'outline'
                }
              >
                {rotuloDeVerificacao(verificacao.verificacao)}
                {verificacao.resultado === 'NAO_CONSULTADO'
                  ? ': não consultado'
                  : verificacao.resultado === 'APROVADO'
                    ? ': ok'
                    : ': reprovado'}
              </Badge>
            ))}
          </div>
        )}

        {item.documentos.length === 0 ? null : (
          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-fg-muted">Documentos pedidos</p>
            <div className="flex flex-wrap gap-2">
              {item.documentos.map((documento) => (
                <Badge
                  key={documento.id}
                  variant={
                    documento.status === 'APROVADO'
                      ? 'success'
                      : documento.status === 'REJEITADO'
                        ? 'danger'
                        : documento.status === 'ENVIADO'
                          ? 'info'
                          : 'warning'
                  }
                >
                  {ROTULO_DO_DOCUMENTO_SOLICITAVEL[documento.tipo]} —{' '}
                  {ROTULO_DO_STATUS_DE_DOCUMENTO[documento.status]}
                </Badge>
              ))}
            </div>
            {item.documentos.some((documento) => documento.status === 'ENVIADO') ? (
              <p className="mt-1 text-xs text-fg-secondary">
                Há documento aguardando avaliação — abra a reserva para aprovar ou rejeitar.
              </p>
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              aprovar.mutate(undefined, {
                onSuccess: () => {
                  toast.success(`Reserva nº ${String(item.reserva.numero)} aprovada.`)
                },
                onError: (erro) => {
                  toast.error(errorMessage(erro))
                },
              })
            }}
            disabled={aprovar.isPending}
          >
            {aprovar.isPending ? <LoaderCircle className="animate-spin" /> : <ShieldCheck />}
            Aprovar
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setRecusando(true)
            }}
          >
            Recusar
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setPedindoDocumento(true)
            }}
          >
            <FileQuestion /> Solicitar documento
          </Button>
        </div>
      </CardContent>

      <Dialog
        open={recusando}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setRecusando(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recusar a reserva nº {item.reserva.numero}</DialogTitle>
            <DialogDescription>O motivo é obrigatório e vai para a trilha.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={motivo}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            placeholder="Por que esta reserva não passa?"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setRecusando(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={motivo.trim().length < 5 || recusar.isPending}
              onClick={() => {
                recusar.mutate(
                  { motivo: motivo.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Reserva recusada.')
                      setRecusando(false)
                      setMotivo('')
                    },
                    onError: (erro) => {
                      toast.error(errorMessage(erro))
                    },
                  },
                )
              }}
            >
              Recusar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pedindoDocumento}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setPedindoDocumento(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar documento adicional</DialogTitle>
            <DialogDescription>
              A terceira ação da fila: nem aprova, nem recusa — pede o que falta. A solicitação fica
              na trilha e o app a exibe ao cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select
              value={tipoDeDocumento}
              onChange={(evento) => {
                setTipoDeDocumento(evento.target.value)
              }}
            >
              {TIPOS_DE_DOCUMENTO.map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </Select>
            <Textarea
              value={instrucoes}
              onChange={(evento) => {
                setInstrucoes(evento.target.value)
              }}
              placeholder="O que o cliente deve enviar, e por quê…"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPedindoDocumento(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={instrucoes.trim().length < 5 || solicitar.isPending}
              onClick={() => {
                solicitar.mutate(
                  {
                    tipo: tipoDeDocumento as 'CNH',
                    instrucoes: instrucoes.trim(),
                  },
                  {
                    onSuccess: () => {
                      toast.success('Solicitação registrada na trilha.')
                      setPedindoDocumento(false)
                      setInstrucoes('')
                    },
                    onError: (erro) => {
                      toast.error(errorMessage(erro))
                    },
                  },
                )
              }}
            >
              Solicitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/** O snapshot guarda a CHAVE; o rótulo vem do catálogo vivo, com fallback. */
function rotuloDeAlerta(chave: string): string {
  return (ROTULO_DO_ALERTA_DE_RISCO as Readonly<Record<string, string>>)[chave] ?? chave
}

function rotuloDeFator(chave: string): string {
  return (ROTULO_DO_FATOR_DE_RISCO as Readonly<Record<string, string>>)[chave] ?? chave
}

function rotuloDeVerificacao(chave: string): string {
  return (ROTULO_DA_VERIFICACAO_EXTERNA as Readonly<Record<string, string>>)[chave] ?? chave
}
